import type { PreloadedQuery } from 'react-relay';
import { fg } from '@atlassian/jira-feature-gating';
import type { RouteResource } from '@atlassian/react-resource-router';
import { getTopNavRelayVariables } from '@jira/navigation-apps__resources/src/services/top-nav/topNav4UIStateResource.tsx';
import { createRelayResource } from '@jira/platform__graphql-relay-utils/src/services/resources/index.tsx';
import queryNode, {
	type useJiraAppChromeQuery as useJiraAppChromeQueryType,
} from './__generated__/useJiraAppChromeQuery.graphql.ts';
import { getSideNavRelayVariables } from './getSideNavRelayVariables.tsx';
import { getStickyProjectsSidebarCurrentUrl } from './getStickyProjectsSidebarCurrentUrl.tsx';

export const appChromeResource: RouteResource<
	PreloadedQuery<useJiraAppChromeQueryType, Record<string, unknown>>
> = createRelayResource<useJiraAppChromeQueryType>({
	type: 'RESOURCE_TYPE_APP_CHROME',
	// Chromeless routes must not trigger this GraphQL query. Use the
	// resource-level `skip` predicate so `loadQuery` is never invoked for them —
	// `getQuery` will not run when `skip` returns `true`.
	//
	// Anonymous users are deliberately NOT skipped (NAVX-5832). `AppChrome` reads
	// this resource through `useSuspenseResource`, and a skipped resource never
	// gains a `promise`: `getData` returns `null` synchronously, which equals the
	// hydrated/default slice's `data` (`null`), so the resource store keeps
	// `promise: null` and `useSuspenseResource` refreshes and re-suspends forever
	// — leaving `AppChromeSkeleton` on screen with no error. GTN also needs real
	// data to render its anonymous mode (`BaseTopNav` renders nothing without
	// `appChromeData`). Anonymous-unsafe selections are excluded inside the query
	// instead: see the `@skip(if: $isAnonymous)` directives in
	// `useJiraAppChromeQuery` and `TopNavigation_slotsQuery`.
	skip: (routerContext) => {
		return shouldSkipAppChromeQuery({
			// Chromeless routes render no top nav and no side nav, so this
			// `isPageShellCritical` query would be SSR-blocking work whose data is never
			// read. `route.layout` is the same plain-object layout config that
			// drives the renderer's `useIsChromeless()`, so the two cannot disagree.
			// `GlobalRoot` does not render `AppChrome` when chromeless, so nothing
			// reads (or suspends on) the skipped resource.
			isChromeless: routerContext.route.layout?.isChromeless === true,
		});
	},
	getQuery: (routerContext, { tenantContext }) => {
		const { cloudId, isAnonymous } = tenantContext;
		const sideNavRelayVariables = getSideNavRelayVariables(routerContext, tenantContext);
		return {
			parameters: queryNode,
			variables: {
				// NAVX-5414: these are the same variables the standalone side-nav resource
				// uses. Keep the helper output authoritative so the two operations cannot
				// silently fetch different data as side-nav requirements evolve.
				...sideNavRelayVariables,
				// SEA-4270: `currentURL` lets the backend resolve the project the user is on,
				// so the projects menu can surface it on a cold landing before it is one of
				// their recents. Supplied here rather than from the shared helper above so it
				// reaches only this operation: it already re-keys on top-nav variables such as
				// `issueKey`, and the read in `useJiraAppChromeQuery` is deferred, so one more
				// route-derived variable costs nothing. The standalone side-nav query has
				// neither property — adding it there would tear the sidebar down mid-navigation.
				...(fg('current_url_in_gtn')
					? { currentURL: getStickyProjectsSidebarCurrentUrl(routerContext, cloudId) }
					: {}),
				// The old record is per-user, so there is nothing to migrate without an owner.
				// Its entity ARI is supplied by the shared side-nav variable helper above.
				fetchOldEntityRecord: Boolean(sideNavRelayVariables.ownerAri),
				skipNavPlatSidebarData: isAnonymous,
				// The GlobalRoot query also spreads `...TopNavigation_slotsQuery` and
				// (via ...AppChrome) GTN's Notifications fragment. Those children read
				// these as root operation variables — including collabContextRoutingAri
				// for the unread-notification count. This resource is registered only
				// behind `fg('navx-global-top-nav-jira')` in `getNavigationResources.tsx`,
				// so fetch the unit profile for GTN's platform-owned AppSwitcher without
				// making it depend on NAVX-5411's legacy measurement experiment. All
				// other callers retain that experiment-driven behavior.
				...getTopNavRelayVariables(routerContext, tenantContext, {
					forceAppSwitcherUnitProfile: true,
				}),
			},
			options: { fetchPolicy: 'store-or-network' },
		};
	},
	isPageShellCritical: true,
});

/**
 * Whether the app-chrome query should be skipped for this render.
 */
function shouldSkipAppChromeQuery({ isChromeless }: { isChromeless: boolean }): boolean {
	return isChromeless;
}
